import React, { useEffect, useState } from "react";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./Spreadsheet.css";

const SpreadsheetAdmin = () => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [showAddNotePopup, setShowAddNotePopup] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteFile, setNoteFile] = useState(null);
  const [showSaveVerificationPopup, setShowSaveVerificationPopup] = useState(false);
  const [rowToSave, setRowToSave] = useState(null);
  const [currentUser, setCurrentUser] = useState(null); // Add state for the signed-in user
  const [showDeleteVerificationPopup, setShowDeleteVerificationPopup] = useState(false); // Add state for deletion verification popup

  useEffect(() => {
    const fetchData = async () => {
      const tasksSnapshot = await getDocs(collection(db, "spreadsheetAdmin"));
      const usersSnapshot = await getDocs(collection(db, "users"));
      const propertiesSnapshot = await getDocs(collection(db, "properties"));

      setRows(tasksSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setUsers(usersSnapshot.docs.map((doc) => doc.data()).sort((a, b) => a.firstName.localeCompare(b.firstName))); // Sort users alphabetically
      setProperties(propertiesSnapshot.docs.map((doc) => doc.data()).sort((a, b) => a.propertyName.localeCompare(b.propertyName))); // Sort properties alphabetically

      // Simulate fetching the signed-in user (replace with actual authentication logic)
      setCurrentUser(usersSnapshot.docs[0]?.data()); // Assume the first user is signed in
    };

    fetchData();
  }, []);

  useEffect(() => {
    const addDummyRow = async () => {
      const dummyRow = {
        task: "Spreadsheet",
        priority: "",
        assignedTo: "",
        status: "",
        association: "",
        startDate: "",
        endDate: "",
        questionsNotes: "",
        notes: [],
      };

      try {
        const tasksSnapshot = await getDocs(collection(db, "spreadsheetAdmin"));
        const existingDummyRow = tasksSnapshot.docs.find(
          (doc) => doc.data().task === "Spreadsheet"
        );

        if (!existingDummyRow) {
          await addDoc(collection(db, "spreadsheetAdmin"), dummyRow);
        }
      } catch (error) {
        console.error("Error adding dummy row:", error);
      }
    };

    addDummyRow();
  }, []);

  const handleCellChange = async (rowId, field, value) => {
    const updatedRows = rows.map((row) =>
      row.id === rowId ? { ...row, [field]: value } : row
    );
    setRows(updatedRows);

    try {
      const taskDoc = doc(db, "spreadsheetAdmin", rowId);
      const updatedRow = updatedRows.find((row) => row.id === rowId);
      await updateDoc(taskDoc, { [field]: value });
    } catch (error) {
      console.error("Error updating cell value:", error);
    }
  };

  const handleSaveRow = (rowId) => {
    const row = rows.find((row) => row.id === rowId);
    setRowToSave(row);
    setShowSaveVerificationPopup(true);
  };

  const confirmSaveRow = async () => {
    if (!rowToSave) return;

    try {
      const taskDoc = doc(db, "spreadsheetAdmin", rowToSave.id);
      await updateDoc(taskDoc, rowToSave);

      const tasksSnapshot = await getDocs(collection(db, "spreadsheetAdmin"));
      setRows(tasksSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

      setShowSaveVerificationPopup(true); // Show confirmation popup after saving
      setRowToSave(null);
    } catch (error) {
      console.error("Error saving row:", error);
    }
  };

  const handleAddRow = async () => {
    const newRow = {
      task: "",
      priority: "",
      assignedTo: "",
      status: "",
      association: "",
      startDate: "",
      endDate: "",
      questionsNotes: "",
      notes: [],
    };

    try {
      const docRef = await addDoc(collection(db, "spreadsheetAdmin"), newRow);
      console.log("New row added to Firestore:", docRef.id); // Debugging log
      setRows([...rows, { id: docRef.id, ...newRow }]);
    } catch (error) {
      console.error("Error adding row:", error);
    }
  };

  const handleShowDetails = (row) => {
    setSelectedRow(row);
    setShowDetailsPopup(true);
  };

  const handleShowDeleteVerificationPopup = () => {
    setShowDeleteVerificationPopup(true);
  };

  const handleCloseDeleteVerificationPopup = () => {
    setShowDeleteVerificationPopup(false);
  };

  const handleConfirmDeleteRow = async () => {
    if (!selectedRow) return;

    try {
      await deleteDoc(doc(db, "spreadsheetAdmin", selectedRow.id));
      setRows(rows.filter((row) => row.id !== selectedRow.id));
      setShowDetailsPopup(false);
      setSelectedRow(null);
      setShowDeleteVerificationPopup(false);
    } catch (error) {
      console.error("Error deleting row:", error);
    }
  };

  const handleAddNote = async () => {
    if (!selectedRow || !currentUser) return;

    try {
      const noteData = {
        noteText,
        noteFile: noteFile ? noteFile.name : null,
        timestamp: new Date(),
        addedBy: `${currentUser.firstName} ${currentUser.lastName}`,
      };

      const updatedRow = {
        ...selectedRow,
        notes: [...(selectedRow.notes || []), noteData],
      };

      const taskDoc = doc(db, "spreadsheetAdmin", selectedRow.id);
      await updateDoc(taskDoc, updatedRow);

      setRows(rows.map((row) => (row.id === selectedRow.id ? updatedRow : row)));
      setShowAddNotePopup(false);
      setNoteText("");
      setNoteFile(null);
    } catch (error) {
      console.error("Error adding note:", error);
    }
  };

  const getRowStyle = (row) => {
    if (row.status === "Completed") {
      return { backgroundColor: "green", color: "white" };
    }

    switch (row.priority) {
      case "Urgent":
        return { backgroundColor: "red", color: "white" };
      case "Proposal":
        return { backgroundColor: "pink", color: "black" };
      case "Needs Scheduling":
        return { backgroundColor: "yellow", color: "black" };
      default:
        return {};
    }
  };

  const moveRowUp = (index) => {
    if (index === 0) return; // Can't move the first row up
    const updatedRows = [...rows];
    [updatedRows[index - 1], updatedRows[index]] = [updatedRows[index], updatedRows[index - 1]];
    setRows(updatedRows);
  };

  const moveRowDown = (index) => {
    if (index === rows.length - 1) return; // Can't move the last row down
    const updatedRows = [...rows];
    [updatedRows[index], updatedRows[index + 1]] = [updatedRows[index + 1], updatedRows[index]];
    setRows(updatedRows);
  };

  return (
    <div className="spreadsheet-container">
      <div className="scrollable-container"> {/* Add a scrollable container */}
        <table className="spreadsheet">
          <thead>
            <tr>
              <th>Task</th>
              <th>Priority</th>
              <th>Assigned To</th>
              <th>Status</th>
              <th>Association</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Questions / Notes</th> {/* Removed "Response / Next Steps" */}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Dummy row */}
            <tr>
              <td colSpan="9" style={{ backgroundColor: "#f0f0f0", textAlign: "center", fontWeight: "bold" }}>
                Spreadsheet
              </td>
            </tr>
            {/* Actual rows */}
            {rows
              .filter((row) => row.task !== "Spreadsheet") // Skip dummy row
              .map((row, index) => (
                <tr key={row.id} style={getRowStyle(row)}>
                  <td>
                    <input
                      type="text"
                      value={row.task}
                      onChange={(e) => handleCellChange(row.id, "task", e.target.value)}
                    />
                  </td>
                  <td>
                    <select
                      value={row.priority}
                      onChange={(e) => handleCellChange(row.id, "priority", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Urgent">Urgent</option>
                      <option value="Pending">Pending</option>
                      <option value="TBD">TBD</option>
                      <option value="Suggestion">Suggestion</option>
                      <option value="Needs Scheduling">Needs Scheduling</option>
                      <option value="Meeting">Meeting</option>
                      <option value="Proposal">Proposal</option>
                      <option value="Supply">Supply</option>
                    </select>
                  </td>
                  <td>
                    <select
                      value={row.assignedTo}
                      onChange={(e) => handleCellChange(row.id, "assignedTo", e.target.value)}
                    >
                      <option value="">Select</option>
                      {users.map((user) => (
                        <option key={user.id} value={`${user.firstName} ${user.lastName}`}>
                          {user.firstName} {user.lastName}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={row.status}
                      onChange={(e) => handleCellChange(row.id, "status", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Not Started">Not Started</option>
                      <option value="In Progress">In Progress</option>
                      <option value="On-Hold">On-Hold</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </td>
                  <td>
                    <select
                      value={row.association}
                      onChange={(e) => handleCellChange(row.id, "association", e.target.value)}
                    >
                      <option value="">Select</option>
                      {properties.map((property) => (
                        <option key={property.id} value={property.propertyName}>
                          {property.propertyName}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="date"
                      value={row.startDate}
                      onChange={(e) => handleCellChange(row.id, "startDate", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={row.endDate}
                      onChange={(e) => handleCellChange(row.id, "endDate", e.target.value)}
                    />
                  </td>
                  <td>
                    <textarea
                      value={row.questionsNotes}
                      onChange={(e) => handleCellChange(row.id, "questionsNotes", e.target.value)}
                    ></textarea>
                  </td>
                  <td>
                    <div className="button-group">
                      <div className="button-row">
                        <button className="details-button" onClick={() => handleShowDetails(row)}>Details</button>
                        <button className="smallgreen-button" onClick={() => handleSaveRow(row.id)}>Save</button>
                      </div>
                      <div className="button-row">
                        <button className="smallgrey-button" onClick={() => moveRowUp(index)}>Up</button>
                        <button className="smallgrey-button" onClick={() => moveRowDown(index)}>Down</button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <button className="add-row-button" onClick={handleAddRow}>
        Add New Row
      </button>

      {/* Details Popup */}
      {showDetailsPopup && selectedRow && (
        <div className="popup">
          <div className="popup-header">
            <h3>Row Details</h3>
            <button className="close-button" onClick={() => setShowDetailsPopup(false)}>X</button>
          </div>
          <div className="popup-content">
            <p><strong>Task:</strong> {selectedRow.task}</p>
            <p><strong>Priority:</strong> {selectedRow.priority}</p>
            <p><strong>Assigned To:</strong> {selectedRow.assignedTo}</p>
            <p><strong>Status:</strong> {selectedRow.status}</p>
            <p><strong>Association:</strong> {selectedRow.association}</p>
            <p><strong>Start Date:</strong> {selectedRow.startDate}</p>
            <p><strong>End Date:</strong> {selectedRow.endDate}</p>
            <p><strong>Questions / Notes:</strong> {selectedRow.questionsNotes}</p>
            <p><strong>Notes:</strong></p>
            <ul>
              {(selectedRow.notes || []).map((note, index) => (
                <li key={index}>
                  <p>{note.noteText}</p>
                  {note.noteFile && <p><em>File:</em> {note.noteFile}</p>}
                  <p><em>Added By:</em> {note.addedBy}</p>
                  <p><em>Timestamp:</em> {new Date(note.timestamp).toLocaleString()}</p>
                </li>
              ))}
            </ul>
          </div>
          <div className="popup-footer">
            <button onClick={() => setShowAddNotePopup(true)}>Add Note</button>
            <button onClick={handleShowDeleteVerificationPopup}>Delete</button>
            <button onClick={() => setShowDetailsPopup(false)}>Close</button>
          </div>
        </div>
      )}

      {/* Add Note Popup */}
      {showAddNotePopup && (
        <div className="popup">
          <div className="popup-header">
            <h3>Add Note</h3>
            <button className="close-button" onClick={() => setShowAddNotePopup(false)}>X</button>
          </div>
          <div className="popup-content">
            <textarea
              placeholder="Enter your note here..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            ></textarea>
            <input
              type="file"
              onChange={(e) => setNoteFile(e.target.files[0])}
            />
          </div>
          <div className="popup-footer">
            <button onClick={handleAddNote}>Save Note</button>
            <button onClick={() => setShowAddNotePopup(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Delete Verification Popup */}
      {showDeleteVerificationPopup && (
        <div className="popup">
          <h3>Are you sure you want to delete this row?</h3>
          <div className="popup-footer">
            <button onClick={handleConfirmDeleteRow}>Yes</button>
            <button onClick={handleCloseDeleteVerificationPopup}>No</button>
          </div>
        </div>
      )}

      {/* Save Verification Popup */}
      {showSaveVerificationPopup && (
        <div className="popup">
          <div className="popup-header">
            <h3>Save Confirmation</h3>
          </div>
          <div className="popup-content">
            <p>Information saved successfully.</p>
          </div>
          <div className="popup-footer">
            <button onClick={() => setShowSaveVerificationPopup(false)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpreadsheetAdmin;